import type { Contract } from '@/types/ContractType';

export function getContractPropertyAddress(
  contract: Contract | undefined | null
): {
  addressLine: string;
  city: string;
  state: string;
  zip: string;
} {
  const product = (contract as any)?.product;
  // Most properties keep the address only in `name` (no address object).
  const raw =
    product?.address ||
    (typeof product?.name === 'string' && product.name.trim()
      ? product.name.trim()
      : undefined);

  const tryParseFromLine = (line: string) => {
    // Expected-ish: "123 Main St, Austin, TX 78701"
    const parts = line
      .split(',')
      .map((p) => p.trim())
      .filter(Boolean);
    const last = parts[parts.length - 1] || '';
    const secondLast = parts[parts.length - 2] || '';

    let parsedCity = '';
    let parsedState = '';
    let parsedZip = '';

    // City is usually second last (if we have at least 3 parts)
    if (parts.length >= 3) {
      parsedCity = secondLast;
    }

    // State/Zip are usually in the last part
    // e.g. "TX 78701" or "TX"
    const stateZipTokens = last.split(/\s+/).filter(Boolean);
    if (stateZipTokens.length >= 1) parsedState = stateZipTokens[0] || '';
    if (stateZipTokens.length >= 2) parsedZip = stateZipTokens[1] || '';

    return { parsedCity, parsedState, parsedZip };
  };

  const legacyAgentAddress = ((contract as any)?.agentSellerInfo
    ?.propertyAddress || '') as string;

  // If legacy data stored address as a string, use it as addressLine.
  const rawIsString = typeof raw === 'string';
  const addressObj = !rawIsString && raw ? raw : null;

  const addressLine =
    (rawIsString ? (raw as string) : '') ||
    (addressObj?.fullAddress as string) ||
    ([addressObj?.street, addressObj?.city, addressObj?.state, addressObj?.zip]
      .filter(Boolean)
      .join(', ') as string) ||
    legacyAgentAddress ||
    '';

  let city = (addressObj?.city || '') as string;
  let state = (addressObj?.state || '') as string;
  let zip = (addressObj?.zip || '') as string;

  // Fill missing structured fields from the address line if possible
  if (addressLine && (!city || !state || !zip)) {
    const { parsedCity, parsedState, parsedZip } =
      tryParseFromLine(addressLine);
    city = city || parsedCity;
    state = state || parsedState;
    zip = zip || parsedZip;
  }

  return {
    addressLine: addressLine || '',
    city: city || '',
    state: state || '',
    zip: zip || '',
  };
}
